import { useState } from "react";

import { Loader2, Sparkles } from "lucide-react";
import { useOrganization } from "../Auth/UnifiedAuthProvider";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";

import { useServerUrl } from "../../hooks/useServerUrl";

interface KnowledgeMatch {
  id: string;
  text: string;
  score: number;
  metadata?: Record<string, unknown>;
}

interface SearchResponse {
  matches: KnowledgeMatch[];
}

function KnowledgeResultItem({ match }: { match: KnowledgeMatch }) {
  const isFAQ = match.metadata?.type === "faq";
  const source = isFAQ ? "FAQ" : "Knowledge Base";
  const displayText = isFAQ
    ? `Q: ${match.metadata?.question}\nA: ${match.metadata?.idealAnswer}`
    : match.text;

  return (
    <div className="border-b p-4 last:border-b-0">
      <div className="mb-2 flex items-start justify-between">
        <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10">
          {source}
        </span>
        <span className="text-sm text-gray-500">
          Score: {Math.round(match.score * 100)}%
        </span>
      </div>
      <div className="whitespace-pre-wrap text-sm">{displayText}</div>
    </div>
  );
}

export function KnowledgeAskUI() {
  const { organization } = useOrganization();
  const serverUrl = useServerUrl();
  const [question, setQuestion] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState<KnowledgeMatch[]>([]);
  const [error, setError] = useState<string | null>(null);

  const handleSearch = async () => {
    if (!organization?.id || !question.trim()) return;

    setIsSearching(true);
    setError(null);
    setResults([]);

    try {
      const response = await fetch(
        `${serverUrl}/parties/main/${organization.id}/api/knowledge/search`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ query: question, orgId: organization.id }),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to search knowledge");
      }

      const result = (await response.json()) as SearchResponse;
      setResults(result.matches);
    } catch (error) {
      console.error("Error searching knowledge:", error);
      setError("Failed to search knowledge base. Please try again.");
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <div className="flex h-full flex-col space-y-4 p-4">
      <div className="flex gap-4">
        <Input
          placeholder="Search knowledge base..."
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSearch();
            }
          }}
        />
        <Button
          onClick={handleSearch}
          disabled={isSearching || !question.trim()}
        >
          {isSearching ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Sparkles className="mr-2 h-4 w-4" />
          )}
          Search
        </Button>
      </div>

      <Card className="flex-grow overflow-hidden">
        <ScrollArea className="h-full">
          {error ? (
            <div className="p-4 text-red-500">{error}</div>
          ) : results.length > 0 ? (
            results.map((match, index) => (
              <KnowledgeResultItem key={`${match.id}-${index}`} match={match} />
            ))
          ) : isSearching ? (
            <div className="p-4 text-gray-500">Searching...</div>
          ) : (
            <div className="p-4 text-gray-500">
              Enter a search term to query the knowledge base
            </div>
          )}
        </ScrollArea>
      </Card>
    </div>
  );
}
